// Emojis, figurinhas e GIFs das Mensagens (0.51).
// Emojis: lista completa por categoria, com busca em português, inglês e espanhol (dados do Emojibase, licença MIT).
// Figurinhas: animadas feitas no próprio app (emoji + frase + animação), de foto, ou da KLIPY.
// GIFs: KLIPY (o serviço de GIFs que substituiu o Tenor do Google). Guardar = fica em "Minhas" para reenviar.
import { getDoc, setDoc } from '../store/store';
import type { AnimFig, Fig, GifSalvo } from '../store/types';
import { uid, norm } from './date';
import { N_, idioma } from './i18n';
import { idAparelho } from './aparelho';
import { KLIPY } from './gifsChave';

// ─── emojis ───
type ItemEmoji = [string, string, string[]?];   // emoji, palavras para a busca, tons de pele
const DADOS: ItemEmoji[][] = require('./emojiDados.json');
export const CATEGORIAS_EMOJI: { icone: string; nome: string }[] = [
  { icone: '😀', nome: N_('Smileys e pessoas') }, { icone: '🐻', nome: N_('Animais e natureza') }, { icone: '🍔', nome: N_('Comidas e bebidas') },
  { icone: '⚽', nome: N_('Atividades') }, { icone: '🚗', nome: N_('Viagens e lugares') }, { icone: '💡', nome: N_('Objetos') },
  { icone: '🔣', nome: N_('Símbolos') }, { icone: '🏳️', nome: N_('Bandeiras') },
];
export const TONS = ['🏻', '🏼', '🏽', '🏾', '🏿'];
export type Emoji = { e: string; peles?: string[] };
const comPele = (x: ItemEmoji, pele: number): Emoji => ({ e: pele && x[2] ? x[2][pele - 1] : x[0], peles: x[2] ? [x[0], ...x[2]] : undefined });
export const emojisDa = (cat: number, pele = 0): Emoji[] => (DADOS[cat] ?? []).map(x => comPele(x, pele));
export function buscarEmojis(q: string, pele = 0): Emoji[] {
  const p = norm(q.trim());
  if (!p) return [];
  const out: Emoji[] = [];
  for (const cat of DADOS) for (const x of cat) {
    if (x[1].split(' ').some(w => w.startsWith(p)) || (p.length > 2 && x[1].includes(p))) out.push(comPele(x, pele));
    if (out.length >= 120) return out;
  }
  return out;
}
export function usarEmoji(e: string) {
  setDoc('figurinhas', d => ({ ...d, emojis: [e, ...(d.emojis ?? []).filter(x => x !== e)].slice(0, 32) }));
}
export const mudarPele = (pele: number) => setDoc('figurinhas', d => ({ ...d, pele }));

// ─── figurinhas animadas ───
export const ANIMACOES: { id: AnimFig; nome: string; icone: string }[] = [
  { id: 'pulo', nome: N_('Pula'), icone: '⤴️' }, { id: 'pulsa', nome: N_('Pulsa'), icone: '💓' }, { id: 'gira', nome: N_('Balança'), icone: '🔄' },
  { id: 'treme', nome: N_('Treme'), icone: '〰️' }, { id: 'flutua', nome: N_('Flutua'), icone: '🎈' }, { id: 'coracoes', nome: N_('Corações'), icone: '💕' },
  { id: 'confete', nome: N_('Confete'), icone: '🎊' }, { id: 'brilho', nome: N_('Brilho'), icone: '✨' }, { id: 'nada', nome: N_('Parada'), icone: '⏸️' },
];
export const CORES_FIG = ['', '#FDE047', '#F472B6', '#60A5FA', '#34D399', '#A78BFA', '#FB923C', '#111827'];
/** Figurinhas prontas (a frase sai no idioma de quem envia). */
export const PRONTAS: { e: string; t: string; a: AnimFig; c?: string }[] = [
  { e: '☀️', t: N_('Bom dia!'), a: 'flutua' }, { e: '🌙', t: N_('Boa noite'), a: 'flutua', c: '#A78BFA' }, { e: '🎉', t: N_('Parabéns!'), a: 'confete' },
  { e: '❤️', t: N_('Te amo'), a: 'coracoes' }, { e: '😂', t: N_('Kkkkk'), a: 'treme' }, { e: '🙏', t: N_('Obrigado!'), a: 'pulsa' },
  { e: '👋', t: N_('Oi!'), a: 'gira' }, { e: '🥳', t: N_('Uhuul!'), a: 'confete', c: '#FDE047' }, { e: '🍻', t: N_('Sextou!'), a: 'pulo' },
  { e: '☕', t: N_('Café?'), a: 'flutua' }, { e: '👍', t: N_('Combinado'), a: 'pulo' }, { e: '🤔', t: N_('Hmm...'), a: 'gira' },
  { e: '🥺', t: N_('Saudade'), a: 'pulsa', c: '#F472B6' }, { e: '🏃', t: N_('Tô chegando'), a: 'treme' }, { e: '😴', t: N_('Zzz'), a: 'flutua' },
  { e: '🔥', t: N_('Arrasou!'), a: 'brilho' }, { e: '💪', t: N_('Bora!'), a: 'pulo', c: '#FB923C' }, { e: '🤗', t: N_('Abraço'), a: 'coracoes' },
  { e: '😱', t: N_('Oi?!'), a: 'treme' }, { e: '✅', t: N_('Feito!'), a: 'brilho', c: '#34D399' }, { e: '🎂', t: N_('Feliz aniversário'), a: 'confete' },
  { e: '🍀', t: N_('Boa sorte!'), a: 'brilho' }, { e: '😘', t: N_('Beijo'), a: 'coracoes' }, { e: '🙌', t: N_('Amém'), a: 'brilho' },
];

/** Guarda nas "Minhas figurinhas" (sem repetir). */
export function guardarFigurinha(f: Omit<Fig, 'id'>) {
  const igual = (x: Fig) => x.e === f.e && x.t === f.t && x.a === f.a && x.img === f.img && x.url === f.url && x.c === f.c;
  setDoc('figurinhas', d => ({ ...d, minhas: [{ ...f, id: uid() }, ...(d.minhas ?? []).filter(x => !igual(x))].slice(0, 200) }));
}
export const apagarFigurinha = (id: string) => setDoc('figurinhas', d => ({ ...d, minhas: (d.minhas ?? []).filter(x => x.id !== id) }));
export function guardarGif(g: Omit<GifSalvo, 'id'>) {
  setDoc('figurinhas', d => ({ ...d, gifs: [{ ...g, id: uid() }, ...(d.gifs ?? []).filter(x => x.url !== g.url)].slice(0, 200) }));
}
export const apagarGif = (id: string) => setDoc('figurinhas', d => ({ ...d, gifs: (d.gifs ?? []).filter(x => x.id !== id) }));
export const minhasFigurinhas = () => getDoc('figurinhas').minhas ?? [];

// ─── KLIPY (GIFs e figurinhas animadas) ───
export const temKlipy = () => KLIPY.length > 10;
export type ItemKlipy = { id: string; url: string; w: number; h: number; prev: string };
const CDN = /^https:\/\/([a-z0-9-]+\.)*klipy\.com\//;

/** Pega o melhor arquivo de cada resultado (a KLIPY manda vários tamanhos e formatos). */
function midia(x: any): ItemKlipy | null {
  const tam = (o: any) => (o && typeof o.url === 'string' && CDN.test(o.url) ? { url: o.url as string, w: +o.width || +o.w || 200, h: +o.height || +o.h || 200 } : null);
  const f = x?.file ?? x?.files;
  const pegar = (nivel: string[], formatos: string[]) => { for (const n of nivel) for (const k of formatos) { const r = tam(f?.[n]?.[k]); if (r) return r; } return null; };
  let g = f ? pegar(['sm', 'md', 'xs', 'hd'], ['gif', 'webp']) : null;
  let p = f ? pegar(['xs', 'sm'], ['gif', 'webp', 'jpg', 'png']) : null;
  const mf = x?.media_formats;   // formato antigo, compatível com o Tenor
  if (!g && mf) g = tam(mf.tinygif) ?? tam(mf.gif) ?? tam(mf.nanogif);
  if (!p && mf) p = tam(mf.nanogif) ?? tam(mf.tinygif);
  if (!g) return null;
  return { id: String(x.id ?? x.slug ?? g.url), url: g.url, w: g.w, h: g.h, prev: (p ?? g).url };
}

let cliente: string | null = null;
export async function buscarKlipy(tipo: 'gifs' | 'stickers', q: string, pagina = 1): Promise<{ itens: ItemKlipy[]; mais: boolean }> {
  if (!temKlipy()) return { itens: [], mais: false };
  cliente ??= (await idAparelho()).replace(/[^a-zA-Z0-9]/g, '').slice(0, 32) || 'app';
  const loc = (idioma() || 'pt-BR').replace('-', '_');
  const ac = q.trim() ? 'search' : 'trending';
  const u = `https://api.klipy.com/api/v1/${encodeURIComponent(KLIPY)}/${tipo}/${ac}?page=${pagina}&per_page=24&customer_id=${cliente}&locale=${encodeURIComponent(loc.includes('_') ? loc : loc + '_' + loc.toUpperCase())}&rating=pg-13`
    + (q.trim() ? `&q=${encodeURIComponent(q.trim().slice(0, 60))}` : '');
  const r = await fetch(u);
  if (!r.ok) throw new Error(`KLIPY ${r.status}`);
  const j = await r.json();
  const lista: any[] = j?.data?.data ?? j?.data ?? j?.results ?? [];
  return { itens: (Array.isArray(lista) ? lista : []).map(midia).filter(Boolean) as ItemKlipy[], mais: !!(j?.data?.has_next ?? j?.next) };
}

// ─── emojis animados (Noto, do Google; licença CC BY 4.0) ───
// Na lista aparece o emoji normal (leve); ao tocar, vai a versão animada como figurinha.
export const ANIMADOS: string[] = [
  "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😭", "😉", "😗", "😙", "😚", "😘", "🥰", "😍", "🤩", "🥳", "🙂", "🙃",
  "😊", "☺️", "😌", "😏", "😋", "😛", "😝", "😜", "🤪", "🥴", "😔", "🥺", "😬", "😑", "😐", "😶", "🤔", "🤫", "🤭", "🥱",
  "🤗", "😱", "🤨", "🧐", "😒", "🙄", "😤", "😠", "😡", "🤬", "😞", "😓", "😟", "😥", "😢", "☹️", "🙁", "😕", "🤐", "😰",
  "😨", "😧", "😦", "😮", "😯", "😲", "😳", "🤯", "😖", "😣", "😩", "😫", "😵", "😴", "😪", "🤤", "🤢", "🤮", "🤧", "🤒",
  "🤕", "🥵", "🥶", "🤑", "🤠", "😎", "🤓", "🥸", "🤥", "🤡", "👻", "💀", "👽", "🤖", "🎃", "💩", "😈", "👿", "😺", "😸",
  "😹", "😻", "😼", "😽", "🙀", "😿", "😾", "🙈", "🙉", "🙊", "👍", "👎", "👏", "🙌", "👐", "🤲", "🤝", "🙏", "✌️", "🤞",
  "🤘", "👌", "👋", "💪", "👀", "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "❣️", "💕", "💞", "💓", "💗", "💖",
  "💘", "💝", "💋", "🔥", "✨", "🌟", "💫", "💥", "💯", "🎉", "🎊", "🎈", "🎂", "🎁", "🏆", "🥇", "⚽", "🏀", "🚀", "🌈",
  "☀️", "🌞", "🌙", "⚡", "❄️", "🌷", "🌹", "🌻", "🍀", "🦋", "🐝", "🐶", "🐱", "🦄", "🐢", "🍕", "🍔", "🍟", "🍿", "☕",
  "🍺", "🍻", "🥂", "🍾", "💃", "🕺", "🎶", "🎵", "💤", "💡", "💰", "📈", "🕒", "✅", "❌", "❓", "❗", "👑", "💎", "🌍",
  "🌊", "🎄", "🎅", "🤶",
];
export const urlAnimado = (e: string) => `https://fonts.gstatic.com/s/e/notoemoji/latest/${[...e].map(c => c.codePointAt(0)!.toString(16)).join('_')}/512.gif`;
export const figurinhaAnimada = (e: string) => ({ a: 'nada' as AnimFig, e, url: urlAnimado(e), w: 512, h: 512 });
/** Buscas rápidas de GIF. */
export const TEMAS_GIF = [N_('Bom dia'), N_('Boa noite'), N_('Parabéns'), N_('Kkkk'), N_('Amor'), N_('Obrigado'), N_('Uau'), N_('Dança'), N_('Triste'), N_('Sim'), N_('Não'), N_('Sextou')];
